import { useEffect } from "react";
import Lenis from "lenis";
import { Toaster, toast } from "sonner";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { HelmetProvider } from "react-helmet-async";
import "@/App.css";

import { AuthProvider } from "@/lib/auth";
import { CartProvider, useCart } from "@/lib/cart";
import { CurrencyProvider } from "@/lib/currency";

import { Home } from "@/pages/Home";
import { ProductDetail } from "@/pages/ProductDetail";
import { VerifyImagesList } from "@/pages/VerifyImagesList";
import { VerifyImagesDetail } from "@/pages/VerifyImagesDetail";
import { Category } from "@/pages/Category";
import { Search } from "@/pages/Search";
import { Login } from "@/pages/Login";
import { Register } from "@/pages/Register";
import { Account } from "@/pages/Account";
import { Checkout } from "@/pages/Checkout";
import { OrderConfirmation } from "@/pages/OrderConfirmation";
import { Journal } from "@/pages/Journal";
import { JournalCategory } from "@/pages/JournalCategory";
import { JournalPost } from "@/pages/JournalPost";
import { AdminDashboard } from "@/pages/AdminDashboard";
import { NotFound } from "@/pages/NotFound";
import { CartDrawer } from "@/components/site/CartDrawer";
import { CelebrationOverlay } from "@/components/site/CelebrationOverlay";
import { ScrollToTop } from "@/components/site/ScrollToTop";
import { SitePage } from "@/pages/SitePage";

const AppShell = () => {
  const { cart, addItem } = useCart();

  useEffect(() => {
    const lenis = new Lenis({
      duration: 0.6,
      easing: (t) => Math.min(1, 1.001 - Math.pow(2, -10 * t)),
      smoothWheel: true,
    });
    const raf = (time) => { lenis.raf(time); requestAnimationFrame(raf); };
    requestAnimationFrame(raf);
    return () => lenis.destroy();
  }, []);

  const handleAdd = (product, qty = 1) => {
    addItem(product, qty);
    toast.success(product.name, {
      description: `Added · ${qty}× $${Number(product.price).toFixed(2)}`,
    });
  };

  const cartCount = cart?.total_qty || 0;

  return (
    <div className="App bg-white text-black antialiased">
      <ScrollToTop />
      <Routes>
        <Route path="/" element={<Home cartCount={cartCount} onAdd={handleAdd} />} />
        <Route path="/category/:slug" element={<Category cartCount={cartCount} onAdd={handleAdd} />} />
        <Route path="/search" element={<Search cartCount={cartCount} onAdd={handleAdd} />} />
        <Route path="/product/:id" element={<ProductDetail cartCount={cartCount} onAdd={handleAdd} />} />

        <Route path="/login" element={<Login />} />
        <Route path="/register" element={<Register />} />
        <Route path="/account" element={<Account />} />
        <Route path="/checkout" element={<Checkout />} />
        <Route path="/order-confirmation" element={<OrderConfirmation />} />
        <Route path="/order-confirmation/:id" element={<OrderConfirmation />} />

        <Route path="/journal" element={<Journal />} />
        <Route path="/journal/category/:slug" element={<JournalCategory />} />
        <Route path="/journal/:slug" element={<JournalPost />} />

        {/* Legacy /blog → /journal (301 preserve SEO link equity) */}
        <Route path="/blog" element={<Navigate to="/journal" replace />} />
        <Route path="/blog/:id" element={<Navigate to="/journal" replace />} />

        <Route path="/admin" element={<AdminDashboard />} />

        <Route path="/verifyimages" element={<VerifyImagesList cartCount={cartCount} />} />
        <Route path="/verifyimages/:id" element={<VerifyImagesDetail cartCount={cartCount} />} />

        {/* Company / Customer / Legal — content in data/sitePages.js */}
        <Route path="/:slug" element={<SitePage />} />

        {/* Catch-all 404 */}
        <Route path="*" element={<NotFound />} />
      </Routes>
      <CartDrawer />
      <CelebrationOverlay />
      <Toaster position="bottom-right" theme="light" />
    </div>
  );
};

function App() {
  return (
    <HelmetProvider>
      <BrowserRouter>
        <AuthProvider>
          <CurrencyProvider>
            <CartProvider>
              <AppShell />
            </CartProvider>
          </CurrencyProvider>
        </AuthProvider>
      </BrowserRouter>
    </HelmetProvider>
  );
}

export default App;
